import { datadogLogs } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum'

const site = 'datadoghq.com'
const service = 'lilypad-web-vuejs'
const env = getEnvironmentVariableValue()
const clientToken = 'pub9730f86ea94be38ec53ad84a0786582e'

/**
 * URL patterns of external resources creating noise
 * on reports and impacting activity metrics are ignored.
 * @see https://docs.datadoghq.com/real_user_monitoring/browser/monitoring_page_performance/#how-page-activity-is-calculated
 */
const ignored3rdParty = [
  /googletagmanager/,
  /google-analytics/,
  /usetiful/
]

if (isTracked()) {
  initializeDatadogRum()
  initializeDatadogLogs()
}

/**
 * Function to determine if an event should be sent to Datadog.
 * @param {Object} event - RumEvent
 * @returns {boolean} Whether or not the event should be sent to Datadog
 */
export function beforeSend (event) {
  const shouldIgnore = ignored3rdParty.some(pattern => pattern.test(event.resource?.url))
  return !shouldIgnore
}

/**
 * Determines if we should initialize Datadog based on the runtime environment.
 * Collecting only on my.max.auto, stage.my.max.auto, and dev.my.max.auto.
 * @returns {boolean} Whether or not Datadog should be initialized in this environment
 */
function isTracked () {
  return process.env.NODE_ENV === 'production' &&
    ['production', 'stage', 'dev'].includes(process.env.DEPLOY_ENV)
}

/**
 * Generates the appropriate Datadog environment value based on `DEPLOY_ENV`.
 * @returns {string} Value to be used as `DD_ENV` for Datadog
 */
function getEnvironmentVariableValue () {
  const mapping = {
    production: 'production',
    stage: 'staging',
    dev: 'development'
  }
  return `max-${mapping[process.env.DEPLOY_ENV]}`
}

/**
 * Datadog Browser Realtime User Monitoring
 * @see: {@link https://docs.datadoghq.com/real_user_monitoring/browser/|Datadog Browser RUM}
 */
function initializeDatadogRum () {
  datadogRum.init({
    applicationId: '115fc3fd-312e-4145-ac16-0456cdf60143',
    clientToken,
    site,
    service, // Used to identify the service in Datadog.
    env,
    version: process.env.SHA, // Used to identify the deployed version in Datadog.
    sessionReplaySampleRate: 20, // Only record some replays to reduce cost.
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'allow',
    beforeSend,
    excludedActivityUrls: ignored3rdParty,
    allowedTracingUrls: [
      /https:\/\/.*\.maxapps\.io/,
      /https:\/\/.*\.firstlook\.biz/
    ]
  })
}

/**
 * Datadog Browser Logs
 * See {@link https://docs.datadoghq.com/logs/log_collection/javascript/}
 */
function initializeDatadogLogs () {
  datadogLogs.init({
    clientToken,
    site,
    forwardErrorsToLogs: false,
    sessionSampleRate: 100,
    env,
    service
  })
}
